import { useState } from "react";
import axios from "axios";
import { Mail, Facebook, Linkedin, Send, CheckCircle2 } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { PRACTICE, HEADWAY } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [status, setStatus] = useState("idle"); // idle | sending | sent | error
    const [error, setError] = useState("");

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setStatus("sending");
        setError("");
        try {
            await axios.post(`${API}/contact`, form, { headers: { "X-Requested-With": "XMLHttpRequest" } });
            setStatus("sent");
            setForm({ name: "", email: "", message: "" });
        } catch (err) {
            setStatus("error");
            setError("Something went wrong — please try again or email us directly.");
        }
    };

    return (
        <div data-testid="contact-page" className="pb-28">
            <PageHeader
                testid="contact-header"
                breadcrumb="Contact Us"
                title="Say hello"
                number="10"
                label="Contact Us"
                heading="Say hello — the kettle's always on"
            >
                <p className="mt-5 max-w-2xl text-base leading-relaxed text-amber-100/70">
                    Questions about therapy, books, trainings, or whether we'd be
                    a good fit? Write to us. For scheduling, forms, and anything
                    personal, Headway keeps it private and simple.
                </p>

                <div className="mt-12 grid gap-10 lg:grid-cols-[0.85fr_1.15fr]">
                    <div>
                        <a
                            href={`mailto:${PRACTICE.email}`}
                            className="flex items-center gap-3 rounded-2xl border border-amber-500/15 bg-[#1F1510]/50 p-5 transition-colors hover:border-amber-400/50"
                        >
                            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-amber-500/15 ring-1 ring-amber-500/30">
                                <Mail className="h-5 w-5 text-amber-400" />
                            </span>
                            <span>
                                <span className="block text-xs uppercase tracking-[0.2em] text-amber-200/50">
                                    Email us
                                </span>
                                <span className="text-sm text-amber-100">
                                    {PRACTICE.email}
                                </span>
                            </span>
                        </a>

                        <div className="mt-5 flex gap-3">
                            <a
                                href={PRACTICE.facebook}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="NeuroPlay Counseling on Facebook"
                                className="flex h-11 w-11 items-center justify-center rounded-full border border-amber-500/20 text-amber-200/80 transition-colors hover:border-amber-400 hover:text-amber-300"
                            >
                                <Facebook className="h-5 w-5" />
                            </a>
                            <a
                                href={PRACTICE.linkedin}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="NeuroPlay Counseling on LinkedIn"
                                className="flex h-11 w-11 items-center justify-center rounded-full border border-amber-500/20 text-amber-200/80 transition-colors hover:border-amber-400 hover:text-amber-300"
                            >
                                <Linkedin className="h-5 w-5" />
                            </a>
                        </div>

                        <p className="mt-6 text-xs leading-relaxed text-amber-100/45">
                            A gentle note: this form is for general questions
                            only. Please don't include health details here —
                            anything personal belongs in your{" "}
                            <a
                                href={HEADWAY}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-amber-300 underline underline-offset-2"
                            >
                                secure Headway portal
                            </a>
                            .
                        </p>
                    </div>

                    <div className="rounded-3xl border border-amber-500/15 bg-[#1F1510]/50 p-8">
                        {status === "sent" ? (
                            <div
                                data-testid="contact-success"
                                className="flex flex-col items-start gap-3"
                            >
                                <CheckCircle2 className="h-10 w-10 text-amber-400" />
                                <p className="font-serif text-2xl text-amber-100">
                                    Thank you — your note is on its way.
                                </p>
                                <p className="text-sm text-amber-100/60">
                                    We reply within a day. Talk soon!
                                </p>
                                <button
                                    onClick={() => setStatus("idle")}
                                    className="mt-3 text-sm font-semibold text-amber-300 hover:text-amber-200"
                                >
                                    Send another message
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="space-y-5">
                                <div>
                                    <label className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-amber-200/50">
                                        Your name
                                    </label>
                                    <input
                                        data-testid="contact-name-input"
                                        required
                                        value={form.name}
                                        onChange={update("name")}
                                        className="w-full rounded-xl border border-amber-500/20 bg-[#150e08] px-4 py-3 text-sm text-amber-50 outline-none transition-colors placeholder:text-amber-100/30 focus:border-amber-400"
                                        placeholder="Jane Doe"
                                    />
                                </div>
                                <div>
                                    <label className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-amber-200/50">
                                        Email
                                    </label>
                                    <input
                                        data-testid="contact-email-input"
                                        required
                                        type="email"
                                        value={form.email}
                                        onChange={update("email")}
                                        className="w-full rounded-xl border border-amber-500/20 bg-[#150e08] px-4 py-3 text-sm text-amber-50 outline-none transition-colors placeholder:text-amber-100/30 focus:border-amber-400"
                                        placeholder="you@example.com"
                                    />
                                </div>
                                <div>
                                    <label className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-amber-200/50">
                                        Message
                                    </label>
                                    <textarea
                                        data-testid="contact-message-input"
                                        required
                                        rows={5}
                                        value={form.message}
                                        onChange={update("message")}
                                        className="w-full resize-none rounded-xl border border-amber-500/20 bg-[#150e08] px-4 py-3 text-sm text-amber-50 outline-none transition-colors placeholder:text-amber-100/30 focus:border-amber-400"
                                        placeholder="How can we help?"
                                    />
                                </div>
                                {status === "error" && (
                                    <p
                                        data-testid="contact-error"
                                        className="text-sm text-red-400"
                                    >
                                        {error}
                                    </p>
                                )}
                                <button
                                    data-testid="contact-submit-button"
                                    type="submit"
                                    disabled={status === "sending"}
                                    className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-7 py-3.5 text-sm font-semibold text-[#1F1510] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-amber-400 disabled:opacity-60"
                                >
                                    <Send className="h-4 w-4" />
                                    {status === "sending"
                                        ? "Sending…"
                                        : "Send message"}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </PageHeader>
        </div>
    );
}
